import { useEffect, useState } from "react";
import type { TocItem } from "./PostBody";
import { cn } from "@/lib/utils";
import { useT } from "@/lib/i18n/I18nProvider";

export function TableOfContents({ items }: { items: TocItem[] }) {
  const { locale } = useT();
  const [activeId, setActiveId] = useState<string>(items[0]?.id ?? "");

  useEffect(() => {
    if (items.length === 0) return;
    const headings = items
      .map((i) => document.getElementById(i.id))
      .filter((el): el is HTMLElement => Boolean(el));
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: "-96px 0px -70% 0px", threshold: 0 },
    );
    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) return null;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      history.replaceState(null, "", `#${id}`);
      setActiveId(id);
    }
  };

  return (
    <nav aria-label={locale === "bg" ? "Съдържание" : "Table of contents"} className="sticky top-24">
      <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {locale === "bg" ? "В тази статия" : "On this page"}
      </p>
      <ul className="space-y-2 border-l border-border/60">
        {items.map((item) => {
          const isActive = activeId === item.id;
          return (
            <li key={item.id} className={cn(item.level === 3 && "ml-3")}>
              <a
                href={`#${item.id}`}
                onClick={(e) => handleClick(e, item.id)}
                className={cn(
                  "block -ml-px border-l-2 py-1 pl-3 text-[13px] leading-snug transition-colors",
                  isActive
                    ? "border-accent text-foreground font-medium"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {item.text}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
